import type { ReactNode } from 'react';
import type { CandidateFormState, FormErrors, FormMode } from '../lib/candidateForm.ts';
import { GRADES, withGroup } from '../lib/candidateForm.ts';
import {
  COLLEGE_LEVELS,
  type Group,
  GROUPS,
  OPEN_SUBTYPES,
  DISTRICTS_SORTED,
  GENDERS,
  LANGUAGES,
  QUALIFICATIONS,
  STATES,
} from '../lib/enums.ts';
import { todayISO } from '../lib/format.ts';
import { Field, Select, TextArea, TextInput } from './Field.tsx';

type Setter = (next: CandidateFormState) => void;

function Section({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <fieldset className='form-section'>
      <legend>{title}</legend>
      {note && <p className='form-section-note'>{note}</p>}
      <div className='field-grid'>{children}</div>
    </fieldset>
  );
}

/**
 * The candidate detail form, shared by:
 *   register     → public registration (group picked on the previous step)
 *   admin-create → admin portal "new candidate" (group picked inline)
 *   admin-edit   → admin portal edit (only fields the backend lets you change)
 */
export function CandidateFields({
  value: f,
  onChange,
  errors: e,
  mode,
  idPrefix = 'f',
}: {
  value: CandidateFormState;
  onChange: Setter;
  errors: FormErrors;
  mode: FormMode;
  idPrefix?: string;
}) {
  const id = (k: string) => `${idPrefix}_${k.replace('.', '_')}`;
  const set = <K extends keyof CandidateFormState>(k: K, v: CandidateFormState[K]) => onChange({ ...f, [k]: v });
  const setSub = <S extends 'school' | 'college' | 'open'>(s: S, k: keyof CandidateFormState[S], v: string) =>
    onChange({ ...f, [s]: { ...f[s], [k]: v } });
  const editing = mode === 'admin-edit';

  // Eligibility windows are per group: शालेय गट has only a lower bound; खुला गट is a closed range.
  const dobRange = f.group === 'school'
    ? { min: '2009-01-01', max: todayISO() }
    : f.group === 'open'
    ? { min: '1982-01-01', max: '2008-12-31' }
    : { min: '1915-01-01', max: todayISO() };

  // Open group only: college student → candidate_type 'college'; working → 'open'.
  const subtypeSection = !editing && f.group === 'open' && (
        <Section title='आपण कोण आहात?'>
          <div className='span-2'>
            <div className={`subtype ${e.candidate_type ? 'has-error' : ''}`} role='radiogroup' aria-label='खुला गट — प्रकार'>
              {OPEN_SUBTYPES.map((o) => {
                const on = f.candidate_type === o.value;
                return (
                  <label key={o.value} className={`subtype__opt ${on ? 'is-on' : ''}`}>
                    <input type='radio' name={id('subtype')} value={o.value} checked={on}
                      onChange={() => set('candidate_type', o.value)} />
                    <span className='subtype__text'>
                      <strong>{o.label}</strong>
                      <span>{o.desc}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {e.candidate_type && <p className='field-error'>{e.candidate_type}</p>}
          </div>
        </Section>
        );

  return (
    <div className='candidate-fields'>
      {mode === 'register' && subtypeSection}
      <Section title='मूलभूत माहिती'>
        <Field id={id('name')} label='पूर्ण नाव' required error={e.name} span2 hint='प्रमाणपत्रावर हेच नाव छापले जाईल.'>
          <TextInput id={id('name')} value={f.name} maxLength={150} autoComplete='name'
            invalid={!!e.name} onChange={(ev) => set('name', ev.target.value)} />
        </Field>
        <Field id={id('dob')} label='जन्मतारीख' required error={e.dob}>
          <TextInput id={id('dob')} type='date' value={f.dob} max={dobRange.max} min={dobRange.min}
            invalid={!!e.dob} onChange={(ev) => set('dob', ev.target.value)} />
        </Field>
        <Field id={id('gender')} label='लिंग' required error={e.gender}>
          <Select id={id('gender')} options={GENDERS} value={f.gender} invalid={!!e.gender}
            onChange={(ev) => set('gender', ev.target.value as CandidateFormState['gender'])} />
        </Field>
        <Field id={id('language')} label='स्पर्धेसाठी निवडलेली भाषा' required error={e.language}>
          <Select id={id('language')} options={LANGUAGES} value={f.language} invalid={!!e.language}
            onChange={(ev) => set('language', ev.target.value as CandidateFormState['language'])} />
        </Field>
      </Section>

      <Section title='संपर्क तपशील'
        note={editing ? 'मोबाईल क्रमांक व ईमेल सर्व्हरवर बदलता येत नाहीत. व्हॉट्सअ‍ॅप क्रमांक बदलता येतो, पण काढून टाकता येत नाही.' : undefined}>
        {!editing && (
          <Field id={id('phone')} label='मोबाईल क्रमांक' required error={e.phone} hint='१० अंकी क्रमांक. स्पर्धकाशी संपर्क साधण्यासाठी किंवा पत्रव्यवहारासाठी हा क्रमांक वापरला जाईल.'>
            <TextInput id={id('phone')} type='tel' inputMode='numeric' value={f.phone} maxLength={14}
              placeholder='98XXXXXXXX' autoComplete='tel-national' invalid={!!e.phone}
              onChange={(ev) => set('phone', ev.target.value)} />
          </Field>
        )}
        <Field id={id('whatsapp')} label='व्हॉट्सअ‍ॅप क्रमांक' error={e.whatsapp_phone}
          hint={editing ? undefined : 'रिकामे सोडल्यास आपला मोबाईल क्रमांकच व्हॉट्सअ‍ॅप क्रमांक म्हणून गृहीत धरला जाईल.'}>
          <TextInput id={id('whatsapp')} type='tel' inputMode='numeric' value={f.whatsapp_phone} maxLength={14}
            invalid={!!e.whatsapp_phone} onChange={(ev) => set('whatsapp_phone', ev.target.value)} />
        </Field>
        {!editing && (
          <Field id={id('email')} label='ईमेल पत्ता' error={e.email} span2 hint="स्पर्धकाशी संपर्क साधण्यासाठी किंवा पत्रव्यवहारासाठी हा e-mail id वापरला जाईल">
            <TextInput id={id('email')} type='email' value={f.email} maxLength={150} autoComplete='email'
              invalid={!!e.email} onChange={(ev) => set('email', ev.target.value)} />
          </Field>
        )}
      </Section>

      {!editing && (
        <Section title='पत्ता'
          note='हा आपला सध्याचा राहण्याचा (correspondence) पत्ता भरा — जिथे आपण सध्या राहता व स्पर्धेच्या वेळीही राहणार आहात. याच पत्त्यावरून, तसेच शालेय गटासाठी शाळेच्या पत्त्यावरूनही, आपले परीक्षा केंद्र ठरवले जाईल.'>
          <Field id={id('address')} label='संपूर्ण पत्ता' required error={e.address} span2>
            <TextArea id={id('address')} value={f.address} rows={2} autoComplete='street-address'
              invalid={!!e.address} onChange={(ev) => set('address', ev.target.value)} />
          </Field>
          <Field id={id('city')} label='गाव / शहर' required error={e.city}>
            <TextInput id={id('city')} value={f.city} maxLength={100} autoComplete='address-level2'
              invalid={!!e.city} onChange={(ev) => set('city', ev.target.value)} />
          </Field>
          <Field id={id('locality')} label='नगर' required error={e.locality}>
            <TextInput id={id('locality')} value={f.locality} maxLength={100}
              invalid={!!e.locality} onChange={(ev) => set('locality', ev.target.value)} />
          </Field>
          <Field id={id('district')} label='जिल्हा' required error={e.district}>
            <Select id={id('district')} options={DISTRICTS_SORTED} value={f.district} invalid={!!e.district}
              onChange={(ev) => set('district', ev.target.value)} />
          </Field>
          <Field id={id('state')} label='राज्य' required hint='सध्या ही स्पर्धा फक्त महाराष्ट्रासाठी आहे.'>
            <Select id={id('state')} options={STATES} value={f.state} placeholder='' disabled
              onChange={() => {}} />
          </Field>
          <Field id={id('pincode')} label='पिनकोड' required error={e.pincode}>
            <TextInput id={id('pincode')} inputMode='numeric' value={f.pincode} maxLength={6}
              autoComplete='postal-code' invalid={!!e.pincode}
              onChange={(ev) => set('pincode', ev.target.value.replace(/\D/g, ''))} />
          </Field>
        </Section>
      )}

      {mode === 'admin-create' && (
        <Section title='गट'>
          <Field id={id('group')} label='गट' required error={e.group}>
            <Select id={id('group')} options={GROUPS} value={f.group} invalid={!!e.group}
              onChange={(ev) => ev.target.value && onChange(withGroup(f, ev.target.value as Group))} />
          </Field>
        </Section>
      )}

      {mode === 'admin-create' && subtypeSection}

      {f.candidate_type === 'school' && (
        <Section title='शालेय तपशील'>
          <Field id={id('school.grade')} label='इयत्ता' required error={e['school.grade']}>
            <Select id={id('school.grade')} options={GRADES} value={f.school.grade} invalid={!!e['school.grade']}
              onChange={(ev) => setSub('school', 'grade', ev.target.value)} />
          </Field>
          <Field id={id('school.school_name')} label='शाळेचे नाव' required error={e['school.school_name']}>
            <TextInput id={id('school.school_name')} value={f.school.school_name} maxLength={200}
              invalid={!!e['school.school_name']} onChange={(ev) => setSub('school', 'school_name', ev.target.value)} />
          </Field>
          <Field id={id('school.school_address')} label='शाळेचा पत्ता' required error={e['school.school_address']} span2>
            <TextInput id={id('school.school_address')} value={f.school.school_address} maxLength={300}
              invalid={!!e['school.school_address']} onChange={(ev) => setSub('school', 'school_address', ev.target.value)} />
          </Field>
          <Field id={id('school.school_locality')} label='शाळेचे नगर' required error={e['school.school_locality']}>
            <TextInput id={id('school.school_locality')} value={f.school.school_locality} maxLength={100}
              invalid={!!e['school.school_locality']} onChange={(ev) => setSub('school', 'school_locality', ev.target.value)} />
          </Field>
          <Field id={id('school.school_city')} label='शाळेचे गाव / शहर' required error={e['school.school_city']}>
            <TextInput id={id('school.school_city')} value={f.school.school_city} maxLength={100}
              invalid={!!e['school.school_city']} onChange={(ev) => setSub('school', 'school_city', ev.target.value)} />
          </Field>
          <Field id={id('school.school_district')} label='शाळेचा जिल्हा' required error={e['school.school_district']}>
            <Select id={id('school.school_district')} options={DISTRICTS_SORTED} value={f.school.school_district}
              invalid={!!e['school.school_district']} onChange={(ev) => setSub('school', 'school_district', ev.target.value)} />
          </Field>
          <Field id={id('school.school_pincode')} label='शाळेचा पिनकोड' required error={e['school.school_pincode']}>
            <TextInput id={id('school.school_pincode')} inputMode='numeric' value={f.school.school_pincode} maxLength={6}
              invalid={!!e['school.school_pincode']}
              onChange={(ev) => setSub('school', 'school_pincode', ev.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field id={id('school.school_phone')} label='शाळेचा दूरध्वनी क्रमांक' required error={e['school.school_phone']}>
            <TextInput id={id('school.school_phone')} type='tel' inputMode='tel' value={f.school.school_phone} maxLength={16}
              placeholder='98XXXXXXXX' invalid={!!e['school.school_phone']} onChange={(ev) => setSub('school', 'school_phone', ev.target.value)} />
          </Field>
          <Field id={id('school.school_email')} label='शाळेचा ईमेल' required error={e['school.school_email']}>
            <TextInput id={id('school.school_email')} type='email' value={f.school.school_email} maxLength={150}
              invalid={!!e['school.school_email']} onChange={(ev) => setSub('school', 'school_email', ev.target.value)} />
          </Field>
        </Section>
      )}

      {f.candidate_type === 'college' && (
        <Section title='महाविद्यालयीन तपशील'>
          <Field id={id('college.level')} label='स्तर' required error={e['college.level']}>
            <Select id={id('college.level')} options={COLLEGE_LEVELS} value={f.college.level} invalid={!!e['college.level']}
              onChange={(ev) => setSub('college', 'level', ev.target.value)} />
          </Field>
          <Field id={id('college.year_of_study')} label='अभ्यासाचे वर्ष' required error={e['college.year_of_study']} hint='उदा. द्वितीय वर्ष असल्यास २ लिहा.'>
            <TextInput id={id('college.year_of_study')} type='number' inputMode='numeric' min={1} max={10}
              value={f.college.year_of_study} invalid={!!e['college.year_of_study']}
              onChange={(ev) => setSub('college', 'year_of_study', ev.target.value)} />
          </Field>
          <Field id={id('college.degree')} label='पदवी / अभ्यासक्रमाचे नाव' required error={e['college.degree']}>
            <TextInput id={id('college.degree')} value={f.college.degree} maxLength={200} placeholder='उदा. बी.ए. (इतिहास)'
              invalid={!!e['college.degree']} onChange={(ev) => setSub('college', 'degree', ev.target.value)} />
          </Field>
          <Field id={id('college.institution_name')} label='महाविद्यालय / संस्थेचे नाव' required error={e['college.institution_name']}>
            <TextInput id={id('college.institution_name')} value={f.college.institution_name} maxLength={200}
              invalid={!!e['college.institution_name']} onChange={(ev) => setSub('college', 'institution_name', ev.target.value)} />
          </Field>
        </Section>
      )}

      {f.candidate_type === 'open' && (
        <Section title='नोकरी / व्यवसाय तपशील'>
          <Field id={id('open.qualification')} label='शैक्षणिक पात्रता' required error={e['open.qualification']}>
            <Select id={id('open.qualification')} options={QUALIFICATIONS} value={f.open.qualification}
              invalid={!!e['open.qualification']} onChange={(ev) => setSub('open', 'qualification', ev.target.value)} />
          </Field>
          <Field id={id('open.profession')} label='व्यवसाय' required error={e['open.profession']}>
            <TextInput id={id('open.profession')} value={f.open.profession} maxLength={150}
              invalid={!!e['open.profession']} onChange={(ev) => setSub('open', 'profession', ev.target.value)} />
          </Field>
        </Section>
      )}
    </div>
  );
}
